import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import {
  DetailsRow,
  IColumn,
  Link,
  ScrollablePane,
  Stack,
  Text,
  useTheme
} from "@bps/fluent-ui";
import { IdentityIcon } from "@components/IdentityIcon";
import { ShimmeredDetailsList } from "@components/tables/ShimmeredDetailsList";
import {
  DeploymentRingFieldDevice,
  DeploymentRingFieldDeviceDetail
} from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { useFieldDeviceDeploymentRings } from "@libs/api/gateways/field/field-ops-gateway.hooks";

import { RingDevicesAssignedTableFilterValues } from "./RingDevicesAssignedTableFilter";

export interface RolloutDevicesAssignedTableProps {
  deploymentRingId: string;
  filter: RingDevicesAssignedTableFilterValues;
}

export const RolloutDevicesAssignedTable = ({
  deploymentRingId,
  filter
}: RolloutDevicesAssignedTableProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { data: devicesAssigned } = useFieldDeviceDeploymentRings(
    undefined,
    deploymentRingId
  );

  const columns: IColumn[] = useMemo(() => {
    return [
      {
        key: "Devices",
        name: "Devices",
        minWidth: 150,
        maxWidth: 800,
        isResizable: true,
        onRender: (dto: DeploymentRingFieldDeviceDetail) => (
          <Stack horizontal tokens={{ childrenGap: theme.spacing.s1 }}>
            <IdentityIcon id={dto.fieldDeviceId} size={18} />
            <Link
              onClick={() =>
                navigate(`/field-mgmt/${dto.fieldDeviceId}/fd-overview`)
              }
            >
              {dto.fieldDeviceId}
            </Link>
          </Stack>
        )
      }
    ];
  }, [navigate, theme]);

  return (
    <Stack tokens={{ childrenGap: theme.spacing.m }}>
      <Stack
        horizontal
        horizontalAlign="space-between"
        verticalAlign="center"
        tokens={{ childrenGap: theme.spacing.m }}
      >
        <Text variant="xLarge">Devices Assigned</Text>
        <Stack.Item align="end">
          <Text
            styles={{
              root: {
                background: theme.semanticColors.listItemBackgroundChecked,
                width: "fit-content",
                padding: 2
              }
            }}
          >
            Total Devices: {devicesAssigned?.length}{" "}
          </Text>
        </Stack.Item>
      </Stack>

      <ScrollablePane styles={{ root: { height: 800, position: "relative" } }}>
        <ShimmeredDetailsList
          stickyHeader
          shimmerLines={5}
          columns={columns}
          onShouldVirtualize={() => true}
          onRenderRow={row => (row ? <DetailsRow {...row} /> : null)}
          items={filterAndSortDevices(devicesAssigned ?? [], filter)}
        />
      </ScrollablePane>
    </Stack>
  );
};

const filterAndSortDevices = (
  rolloutSoftwarePackageVersions: DeploymentRingFieldDevice[],
  filter: RingDevicesAssignedTableFilterValues
): DeploymentRingFieldDevice[] => {
  return rolloutSoftwarePackageVersions
    .filter(
      d =>
        filter.fieldDeviceId === "" ||
        d.fieldDeviceId
          .toLowerCase()
          .indexOf(filter.fieldDeviceId.toLowerCase()) >= 0
    )
    .sort((a: DeploymentRingFieldDevice, b: DeploymentRingFieldDevice) => {
      return a.id < b.id ? 1 : -1;
    });
};
